"use client";

import type { CSSProperties } from "react";
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore";
import type { ChatInterfaceRef } from "@/components/chat/types/chatInterface.types";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useIsPhone } from "@/hooks/useIsPhone";
import { FeatureRequestButton } from "./FeatureRequestButton";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { readLocalStorage, writeLocalStorage } from "@/utils/localStorage.utils";
import { Button, ContactAvatar, Glyph, Menu, SkeletonText, Tooltip, WilsonFace } from "@/components/desktop/ui";
import type { MenuItem } from "@/components/desktop/ui";
import { useAccountMenu } from "./useAccountMenu";
import { HOME_CHAT_EVENT, WORK_LINE_ON_PAGE, isHomeChatRequest, parseIsListOpen, partOfDay } from "./chatHome.utils";
const ChatInterface = lazy(() => import("@/components/ChatInterface").then(mod => ({
  default: mod.ChatInterface
})));
const TodaySummary = lazy(() => import("./TodaySummary").then(mod => ({
  default: mod.TodaySummary
})));
const RailSections = lazy(() => import("./RailSections").then(mod => ({
  default: mod.RailSections
})));
const LIST_OPEN_KEY = "wilson.chatHome.isConversationListOpen";
const MAIN_PANE_CLEARS_BARS = `pb-28 pt-14 ${WORK_LINE_ON_PAGE}`;
const LIST_SHOWN_FROM_WIDTH = 1024;
const COLUMN_GUTTERS = 48;
const CHAT_COLUMN_WIDTH = {
  withList: 720,
  withoutList: 840
};
const TRANSCRIPT_WIDTH = {
  withList: 652,
  withoutList: 772
};
function chatColumnStyle({
  isListOpen,
  listWidth
}: {
  isListOpen: boolean;
  listWidth: number;
}): CSSProperties & Record<`--${string}`, string> {
  const width = isListOpen ? "withList" : "withoutList";
  return {
    marginRight: isListOpen ? `clamp(0px, calc(100vw - ${listWidth + CHAT_COLUMN_WIDTH[width] + COLUMN_GUTTERS}px), ${listWidth}px)` : 0,
    "--chat-column-width": `${CHAT_COLUMN_WIDTH[width]}px`,
    "--chat-transcript-width": `${TRANSCRIPT_WIDTH[width]}px`,
    "--undo-toast-top": "16px",
    "--undo-toast-bottom": "auto"
  };
}
function useIsListOpen({
  isPhone
}: {
  isPhone: boolean;
}) {
  const [isListOpen, setIsListOpen] = useState(!isPhone);
  useEffect(() => {
    if (isPhone) {
      setIsListOpen(false);
      return;
    }
    const stored = readLocalStorage({
      key: LIST_OPEN_KEY,
      parse: parseIsListOpen
    });
    setIsListOpen(stored ?? window.innerWidth >= LIST_SHOWN_FROM_WIDTH);
  }, [isPhone]);
  const changeIsListOpen = useCallback((isOpen: boolean) => {
    setIsListOpen(isOpen);
    if (!isPhone) {
      writeLocalStorage({
        key: LIST_OPEN_KEY,
        value: String(isOpen)
      });
    }
  }, [isPhone]);
  return [isListOpen, changeIsListOpen] as const;
}
function Greeting({
  isPhone
}: {
  isPhone: boolean;
}) {
  const t = useClientTranslations("desktop.chatHome");
  const {
    data: me
  } = api.user.getMyUser.useQuery();
  const part = partOfDay(new Date());
  const name = me?.firstName?.trim();
  return <h1 className={cn("flex items-center justify-center font-normal tracking-tight text-ink", isPhone ? "gap-3 text-figure" : "gap-4 text-display")} data-sentry-component="Greeting" data-sentry-source-file="ChatHome.tsx">
            <WilsonFace variant="bare" className={cn("flex-shrink-0", isPhone ? "h-7 w-7" : "h-9 w-9")} data-sentry-element="WilsonFace" data-sentry-source-file="ChatHome.tsx" />
            <span>
                {name ? t(`greeting.${part}`, {
        name
      }) : t(`greeting.${part}NoName`)}
            </span>
        </h1>;
}
function ListHeader({
  onHide
}: {
  onHide: () => void;
}) {
  const t = useClientTranslations("desktop.chatHome");
  return <div className="flex items-center gap-2.5 px-4 pt-4" data-sentry-component="ListHeader" data-sentry-source-file="ChatHome.tsx">
            <WilsonFace variant="bare" className="h-5 w-5 text-ink" data-sentry-element="WilsonFace" data-sentry-source-file="ChatHome.tsx" />
            <span className="flex-1 text-headline font-medium text-ink">
                {t("wilson")}
            </span>
            <FeatureRequestButton data-sentry-element="FeatureRequestButton" data-sentry-source-file="ChatHome.tsx" />
            <Tooltip side="bottom" content={t("hideConversations")} data-sentry-element="Tooltip" data-sentry-source-file="ChatHome.tsx">
                <Button variant="ghost" size="icon" onClick={onHide} aria-label={t("hideConversations")} data-sentry-element="Button" data-sentry-source-file="ChatHome.tsx">
                    <Glyph name="sidebar" data-sentry-element="Glyph" data-sentry-source-file="ChatHome.tsx" />
                </Button>
            </Tooltip>
        </div>;
}
function withSeparators(groups: MenuItem[][]): MenuItem[] {
  return groups.filter(group => group.length > 0).flatMap((group, groupIndex) => group.map((item, index) => ({
    ...item,
    hasSeparatorBefore: groupIndex > 0 && index === 0
  })));
}
function ListFooter() {
  const t = useClientTranslations("desktop.chatHome");
  const {
    data: me
  } = api.user.getMyUser.useQuery();
  const {
    data: org
  } = api.org.getMyOrg.useQuery();
  const {
    orgGroup,
    themeGroup,
    logoutGroup
  } = useAccountMenu();
  const name = [me?.firstName, me?.lastName].filter(Boolean).join(" ");
  if (!me) return null;
  return <div className="mx-3 border-t border-ink/[0.07] py-2" data-sentry-component="ListFooter" data-sentry-source-file="ChatHome.tsx">
            <Menu label={t("account")} align="start" items={withSeparators([orgGroup, themeGroup, logoutGroup])} triggerClassName="h-auto w-full justify-start gap-3 rounded-row px-1 py-1.5 text-left" trigger={<>
                        <ContactAvatar name={name || me.email} sizeClass="h-8 w-8" textClass="text-footnote" />
                        <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-body text-ink">
                                {name || me.email}
                            </span>
                            {org?.name && <span className="truncate text-footnote text-ink-2">
                                    {org.name}
                                </span>}
                        </span>
                        <Glyph name="chevronsUpDown" className="h-4 w-4 flex-shrink-0 text-ink-3" />
                    </>} data-sentry-element="Menu" data-sentry-source-file="ChatHome.tsx" />
        </div>;
}
export function ChatHomeSkeleton() {
  const t = useClientTranslations("desktop.chatHome");
  return <div className="flex h-full flex-col items-center justify-center gap-8 pb-24" data-sentry-component="ChatHomeSkeleton" data-sentry-source-file="ChatHome.tsx">
            <SkeletonText className="text-display" data-sentry-element="SkeletonText" data-sentry-source-file="ChatHome.tsx">
                {t("greeting.afternoonNoName")}
            </SkeletonText>
            <div className="h-28 w-full max-w-[720px] rounded-panel bg-ink/[0.05]" />
        </div>;
}
export function ChatHome() {
  const t = useClientTranslations("desktop.chatHome");
  const isPhone = useIsPhone();
  const [isListOpen, setIsListOpen] = useIsListOpen({
    isPhone
  });
  const [listWidth, setListWidth] = useState(0);
  const chatRef = useRef<ChatInterfaceRef>(null);
  const isDraftRef = useRef(true);
  useEffect(() => {
    const leaveConversationOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (isDraftRef.current) return;
      if (getStagedWindow(useDesktopStore.getState().windows)) return;
      event.preventDefault();
      chatRef.current?.startNewConversation();
    };
    window.addEventListener("keydown", leaveConversationOnEscape);
    return () => window.removeEventListener("keydown", leaveConversationOnEscape);
  }, []);
  const [autoSend, setAutoSend] = useState<{
    id: string;
    text: string;
  } | null>(null);
  const threadIdAwaitingChatRef = useRef<string | null>(null);
  const attachChat = useCallback((chat: ChatInterfaceRef | null) => {
    chatRef.current = chat;
    const threadId = threadIdAwaitingChatRef.current;
    if (!chat || !threadId) return;
    threadIdAwaitingChatRef.current = null;
    chat.selectConversation(threadId);
  }, []);
  useEffect(() => {
    const goToChat = (event: Event) => {
      const request = event instanceof CustomEvent ? event.detail : undefined;
      if (!isHomeChatRequest(request)) return;
      const chat = chatRef.current;
      if (!chat && request.threadId) {
        threadIdAwaitingChatRef.current = request.threadId;
      }
      if (request.threadId) chat?.selectConversation(request.threadId);else if (request.question) chat?.startNewConversation();
      if (request.question) {
        setAutoSend({
          id: `home-ask-${Date.now()}`,
          text: request.question
        });
      }
      chat?.focusInput();
    };
    window.addEventListener(HOME_CHAT_EVENT, goToChat);
    return () => window.removeEventListener(HOME_CHAT_EVENT, goToChat);
  }, []);
  return <div className="relative h-full bg-offwhite" data-sentry-component="ChatHome" data-sentry-source-file="ChatHome.tsx">
            {!isListOpen && <div className="absolute left-4 top-4 z-10 flex items-center gap-1">
                    <Tooltip side="bottom" content={t("showConversations")}>
                        <Button variant="ghost" size="icon" onClick={() => setIsListOpen(true)} aria-label={t("showConversations")}>
                            <Glyph name="sidebar" />
                        </Button>
                    </Tooltip>
                    <FeatureRequestButton />
                </div>}
            <Suspense fallback={<ChatHomeSkeleton />} data-sentry-element="Suspense" data-sentry-source-file="ChatHome.tsx">
                <ChatInterface ref={attachChat} onConversationsChange={snapshot => {
        isDraftRef.current = snapshot.isDraft;
      }} autoSendMessage={autoSend} onAutoSendDispatched={() => setAutoSend(null)} shouldStartInDraft isMobile={isPhone} suggestedPrompts={[]} isCompact isConversationListOpen={isListOpen} conversationListChrome={{
        header: <ListHeader onHide={() => setIsListOpen(false)} />,
        footer: <ListFooter />,
        listTail: <Suspense fallback={null}>
                                <RailSections onChoose={isPhone ? () => setIsListOpen(false) : undefined} />
                            </Suspense>,
        onWidthChange: setListWidth,
        isFullScreen: isPhone,
        onChoose: isPhone ? () => setIsListOpen(false) : undefined
      }} emptyHero={<Greeting isPhone={isPhone} />} emptyHeroFooter={<Suspense fallback={null}>
                            <TodaySummary />
                        </Suspense>} mainPaneClassName={MAIN_PANE_CLEARS_BARS} mainPaneStyle={chatColumnStyle({
        isListOpen: isListOpen && !isPhone,
        listWidth
      })} isComposerRaised data-sentry-element="ChatInterface" data-sentry-source-file="ChatHome.tsx" />
            </Suspense>
        </div>;
}